import React, {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";
import { ListarHistoricoCliente } from "../../Controllers/Atendimentos/Atendimento";
import { ListarHistoricosController } from "../../Controllers/Cobranca/GestaoAtendimento/HistoricosControllers";

import { HistoricoClienteInterface } from "../../Interfaces/Geral/Historicos/HistoricoInterface";

interface IHistoricoProviderProps {
    children: JSX.Element | JSX.Element[];
    ID_CONTA: number;
    ID_CASO?: number;
}
interface IHistoricoContext {
    ReloadHistorico: () => Promise<void>;
    LoadHistoricoCliente: () => Promise<Array<HistoricoClienteInterface>>;
    LoadHistoricoCaso: () => Promise<
        Array<HistoricoClienteInterface> | undefined
    >;
    LoadProximoHistoricoCliente: () => void;
    historico: Array<HistoricoClienteInterface>;
    showLoadMoreButton: boolean;
    historicoCaso: Array<HistoricoClienteInterface>;
    loadingHistorico: {
        cliente: TLoadStatus;
        caso: TLoadStatus;
    };
    ID_CONTA: number;
    ID_CASO?: number;
    cadastrarHistoricoRef: React.MutableRefObject<string | undefined>;
}
type TLoadStatus = "preLoad" | "loading" | "loaded";

const HistoricoContext = createContext<IHistoricoContext>(
    {} as IHistoricoContext
);

export function HistoricoProvider(props: IHistoricoProviderProps) {
    const [historico, setHistorico] = useState<Array<HistoricoClienteInterface>>([]);
    const [historicoCaso, setHistoricoCaso] = useState<Array<HistoricoClienteInterface>>([]);
    const [loadingHistorico, setLoadingHistorico] = useState({
        cliente: "preLoad" as TLoadStatus,
        caso: "preLoad" as TLoadStatus,
    });
    const [limitIndex, setLimitIndex] = useState(100);
    const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
    const [historicoAll, setHistoricoAll] = useState<Array<HistoricoClienteInterface>>([]);

    const cadastrarHistoricoRef = useRef<string>();

    const LoadHistoricoCliente = useCallback(async () => {
        setLoadingHistorico((lh) => ({
            ...lh,
            cliente: "loading",
        }));
        return ListarHistoricoCliente(props.ID_CONTA)
            .then((res) => {
                setHistoricoAll(res);
                if(res.length > 100) setShowLoadMoreButton(true);
                return res.slice(0, 100);
            })
            .then((res) => {
                setHistorico(res);
                return res;
            })
            .finally(() =>
                setLoadingHistorico((lh) => ({
                    ...lh,
                    cliente: "loaded",
                }))
            );
    }, [props.ID_CONTA]);

    const LoadProximoHistoricoCliente = useCallback(() => {
        if (limitIndex < historicoAll.length) {
          setLoadingHistorico((lh) => ({ ...lh, cliente: "loading" }));
      
          const limit = 100;
          const startIndex = limitIndex;
          const endIndex = startIndex + limit;
      
          const nextItems = historicoAll.slice(startIndex, endIndex);
      
          setLimitIndex(limitIndex + limit);
      
          setHistorico((prevHistorico) => [...prevHistorico, ...nextItems]);
      
          setLoadingHistorico((lh) => ({ ...lh, cliente: "loaded" }));
          setShowLoadMoreButton(true);
        } else {
          setShowLoadMoreButton(false);
        }
      }, [limitIndex, historicoAll]);
      
    const LoadHistoricoCaso = useCallback(async () => {
        if (props.ID_CASO) {
            setLoadingHistorico((lh) => ({
                ...lh,
                caso: "loading",
            }));
            return ListarHistoricosController(props.ID_CASO)
                .then((res) => {
                    setHistoricoCaso(res);
                    return res;
                })
                .finally(() =>
                    setLoadingHistorico((lh) => ({
                        ...lh,
                        caso: "loaded",
                    }))
                );
        }
    }, [props.ID_CASO]);

    const ReloadHistorico = useCallback(async () => {
        if (loadingHistorico.cliente === "loaded") LoadHistoricoCliente();
        if (loadingHistorico.caso === "loaded") LoadHistoricoCaso();
    }, [
        LoadHistoricoCaso,
        LoadHistoricoCliente,
        loadingHistorico.caso,
        loadingHistorico.cliente,
    ]);

    return (
        <HistoricoContext.Provider
            value={{
                ReloadHistorico,
                LoadHistoricoCaso,
                LoadHistoricoCliente,
                LoadProximoHistoricoCliente,
                showLoadMoreButton,
                loadingHistorico,
                historico,
                historicoCaso,
                cadastrarHistoricoRef,
                ID_CONTA: props.ID_CONTA,
                ID_CASO: props.ID_CASO,
            }}
        >
            {props.children}
        </HistoricoContext.Provider>
    );
}

export function useHistoricoContext() {
    const context = useContext(HistoricoContext);
    if (!context)
        throw new Error(
            "useHistoricoContext necessita de um HistoricoProvider em volta do que sera utilizado"
        );

    return context;
}